/**
 * `document.title` per page (TMMAPP-0072, TMM-B7): "Page · Product". The product name is
 * `useBrand().productName` (trimmed, TMMAPP-0048), so a tenant brand reads its own name.
 *
 * `BrandHead` owns only the fallback — the product name, shown before a page sets one, on the
 * home and pre-app screens, and again when a page unmounts. Entries stack (the newest effect
 * wins, and one entry leaving never blanks another still mounted); a safety net, as no page nests
 * a second titled component today.
 */
import { useEffect, useId } from 'react';
import { useBrand } from '@/theme/useBrand';

interface Entry {
  id: string;
  title: string;
}

const entries: Entry[] = [];
let fallback = '';

function apply(): void {
  document.title = entries.at(-1)?.title ?? fallback;
}

/** `BrandHead`'s hook: the title shown while no page has set one. */
export function setFallbackDocumentTitle(title: string): void {
  fallback = title;
  apply();
}

export function formatDocumentTitle(page: string, productName: string): string {
  return `${page} · ${productName}`;
}

/** Sets "{page} · {product}" for the calling page's lifetime; `null` sets nothing. */
export function useDocumentTitle(page: string | null): void {
  const { productName } = useBrand();
  const id = useId();
  const title = page === null ? null : formatDocumentTitle(page, productName);
  useEffect(() => {
    const at = entries.findIndex((entry) => entry.id === id);
    if (title === null) {
      if (at >= 0) entries.splice(at, 1);
    } else if (at >= 0) {
      entries[at] = { id, title };
    } else {
      entries.push({ id, title });
    }
    apply();
  }, [id, title]);
  useEffect(
    () => () => {
      const at = entries.findIndex((entry) => entry.id === id);
      if (at >= 0) entries.splice(at, 1);
      apply();
    },
    [id],
  );
}
